import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useBoards, useDeleteBoard, BoardFormDialog } from '@/features/boards/public';
import type { Board } from '@/features/boards/public';
import type { Project } from '@/features/projects/public';

interface BoardSubListProps {
  project: Project;
  orgId: string;
  t: ReturnType<typeof useTranslation<'layout'>>['t'];
}

export function BoardSubList({ project, orgId, t }: BoardSubListProps) {
  const navigate = useNavigate();
  const { boards, isLoading } = useBoards(project.id);
  const deleteBoard = useDeleteBoard(project.id);
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  const [editOpen, setEditOpen] = useState(false);
  const [editingBoard, setEditingBoard] = useState<Board | undefined>(undefined);

  const sorted = [...boards].sort((a, b) => a.order - b.order);

  async function handleDelete(board: Board) {
    if (!board.id) return;
    const result = await confirm({
      title: t('sidebar.projects.boards.delete'),
      description: board.name ?? '',
      variant: 'destructive',
    });
    if (!result.confirmed) return;
    try {
      await deleteBoard.mutateAsync(board.id);
    } catch {
      // global handler shows error toast
    }
  }

  if (isLoading) {
    return (
      <div className="ml-6 flex flex-col gap-0.5 py-0.5">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2 px-2 py-1.5">
            <Skeleton className="h-3 w-3 rounded" />
            <Skeleton className="h-3 flex-1" />
          </div>
        ))}
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <p className="ml-6 px-2 py-1.5 text-xs text-muted-foreground">
        {t('sidebar.projects.boards.empty')}
      </p>
    );
  }

  return (
    <>
      <div className="ml-6 flex flex-col gap-0.5 py-0.5">
        {sorted.map((board) => (
          <div
            key={board.id}
            className="group/board flex items-center rounded-lg transition-colors hover:bg-muted"
          >
            <button
              type="button"
              onClick={() => {
                if (board.id) {
                  navigate(`/${orgId}/projects/${project.id}/boards/${board.id}`);
                }
              }}
              className="flex flex-1 min-w-0 items-center gap-2 px-2 py-1.5 text-left"
              data-testid={`projects-sidebar-board-${board.id}`}
            >
              <LayoutGrid className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="truncate text-sm leading-none text-foreground">
                {board.name ?? '—'}
              </span>
            </button>

            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="mr-1 h-5 w-5 shrink-0 opacity-100 md:opacity-0 md:group-hover/board:opacity-100"
                      aria-label={t('sidebar.projects.boards.options')}
                      data-testid={`projects-sidebar-board-${board.id}-options`}
                    >
                      <MoreHorizontal className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>{t('sidebar.projects.boards.options')}</TooltipContent>
              </Tooltip>

              <DropdownMenuContent align="end" side="right">
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => {
                    setEditingBoard(board);
                    setEditOpen(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5 shrink-0" />
                  {t('sidebar.projects.boards.edit')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-2 text-destructive focus:text-destructive"
                  onClick={() => handleDelete(board)}
                >
                  <Trash2 className="h-3.5 w-3.5 shrink-0" />
                  {t('sidebar.projects.boards.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>

      {editOpen && editingBoard && (
        <BoardFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          projectId={project.id}
          board={editingBoard}
        />
      )}

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(v) => !v && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
